import { useEffect, useState } from 'react';

/**
 * A value that changes once a minute (and immediately when a backgrounded
 * tab comes back to the foreground), for use as a `useMemo` dependency that
 * forces a wall-clock re-derivation.
 *
 * Round-1 fix R24. `SignalsFlowTable`'s Local-time column — the badge, its
 * business-hours colour, and the Local-time sort — is derived in a memo
 * keyed on the row set, and the row set does not change for hours in a tab
 * a rep leaves open. Contacts stayed pinned at the time the page happened
 * to load, and "in business hours" stayed green long after their day ended.
 * The only other re-derivation trigger was `useLocalDayToken`, which fires
 * once per local midnight.
 *
 * Its own module rather than a local hook inside `SignalsFlowTable.tsx` so
 * the interval and the listener are testable without mounting the tab.
 *
 * StrictMode-safe by construction (`docs/v4-system/strictmode-resilient-effects.md`):
 * there is no lifetime ref to be left stale across
 * `setup -> cleanup -> setup` — only an interval and a document listener,
 * both torn down in cleanup, so a double-invoked effect ends with exactly
 * one of each. Never add an "already started" ref guard here.
 *
 * The one-minute cadence is deliberate: the badge shows `h:mm`, so a
 * shorter interval would re-render for no visible change and a longer one
 * would let the displayed minute lag.
 */
const MINUTE_MS = 60_000;

export function useMinuteToken(): number {
  const [token, setToken] = useState(() => Date.now());

  useEffect(() => {
    const bump = () => setToken(Date.now());
    const interval = setInterval(bump, MINUTE_MS);
    // A hidden tab's timers are throttled hard by the browser, so coming
    // back to the tab is precisely when the displayed clock is most stale —
    // bump straight away rather than waiting out the next interval.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bump();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return token;
}
